/**
 * Macro Calculator
 * Data-driven calorie and macro calculations for trainers
 */

// =============================================================================
// Types
// =============================================================================

export type Sex = 'male' | 'female';
export type Goal = 'fat_loss' | 'maintenance' | 'muscle_gain';
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';

export interface ClientProfile {
  weight_lbs: number;
  height_inches: number;
  age: number;
  sex: Sex;
  activity_level: ActivityLevel;
  body_fat_percentage?: number; // Optional for more accurate calculations
}

export interface GoalSettings {
  goal: Goal;
  /** Calorie adjustment from TDEE (negative for deficit, positive for surplus) */
  calorie_adjustment: number;
  /** Target protein in grams per pound of body weight */
  protein_per_lb: number;
  /** Target fat in grams per pound of body weight (minimum ~0.3 for health) */
  fat_per_lb: number;
}

export interface MacroTargets {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  water_oz: number;
}

export interface CalculationResult {
  bmr: number;
  tdee: number;
  baseline_source: 'formula' | 'food_log';
  baseline_confidence: 'low' | 'medium' | 'high';
  target_calories: number;
  targets: MacroTargets;
  weekly_change_lbs: number;
  macros_percentage: {
    protein: number;
    carbs: number;
    fat: number;
  };
}

export interface BaselineFromLogs {
  average_calories: number;
  days_tracked: number;
  weight_change_lbs: number;
  confidence: 'low' | 'medium' | 'high';
  start_weight: number;
  end_weight: number;
  is_stable: boolean;
}

// =============================================================================
// Constants
// =============================================================================

/** Activity level multipliers for TDEE calculation */
export const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
  sedentary: 1.2,      // Little or no exercise
  light: 1.375,        // Light exercise 1-3 days/week
  moderate: 1.55,      // Moderate exercise 3-5 days/week
  active: 1.725,       // Hard exercise 6-7 days/week
  very_active: 1.9,    // Very hard exercise, physical job
};

export const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Sedentary (little or no exercise)',
  light: 'Light (1-3 days/week)',
  moderate: 'Moderate (3-5 days/week)',
  active: 'Active (6-7 days/week)',
  very_active: 'Very Active (intense daily + physical job)',
};

export const GOAL_LABELS: Record<Goal, string> = {
  fat_loss: 'Fat Loss',
  maintenance: 'Maintenance',
  muscle_gain: 'Muscle Gain',
};

/** Preset deficit/surplus amounts */
export const CALORIE_PRESETS = {
  fat_loss: {
    conservative: { adjustment: -250, label: 'Conservative', rate: '~0.5 lb/week' },
    moderate: { adjustment: -500, label: 'Moderate', rate: '~1 lb/week' },
    aggressive: { adjustment: -750, label: 'Aggressive', rate: '~1.5 lb/week' },
  },
  maintenance: {
    maintain: { adjustment: 0, label: 'Maintain', rate: 'No change' },
  },
  muscle_gain: {
    lean: { adjustment: 250, label: 'Lean Bulk', rate: '~0.5 lb/week' },
    moderate: { adjustment: 375, label: 'Moderate', rate: '~0.75 lb/week' },
    aggressive: { adjustment: 500, label: 'Aggressive', rate: '~1 lb/week' },
  },
};

/** Protein recommendations based on goal */
export const PROTEIN_RECOMMENDATIONS = {
  fat_loss: { min: 0.8, default: 1.0, max: 1.2, note: 'Higher protein preserves muscle during deficit' },
  maintenance: { min: 0.7, default: 0.8, max: 1.0, note: 'Moderate protein for general health' },
  muscle_gain: { min: 0.8, default: 1.0, max: 1.2, note: 'Higher protein supports muscle growth' },
};

/** Minimum fat intake for hormonal health */
export const MIN_FAT_PER_LB = 0.3;

/** Default fiber target */
export const FIBER_PER_1000_CAL = 14; // 14g per 1000 calories

/** Water recommendation */
export const WATER_OZ_PER_LB = 0.5; // 0.5 oz per lb body weight

// =============================================================================
// Core Calculations
// =============================================================================

/**
 * Calculate BMR using Mifflin-St Jeor equation
 * More accurate than Harris-Benedict for most populations
 */
export function calculateBMR(profile: ClientProfile): number {
  const weight_kg = profile.weight_lbs * 0.453592;
  const height_cm = profile.height_inches * 2.54;

  if (profile.sex === 'male') {
    // Men: BMR = 10W + 6.25H - 5A + 5
    return Math.round(10 * weight_kg + 6.25 * height_cm - 5 * profile.age + 5);
  } else {
    // Women: BMR = 10W + 6.25H - 5A - 161
    return Math.round(10 * weight_kg + 6.25 * height_cm - 5 * profile.age - 161);
  }
}

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 */
export function calculateTDEE(profile: ClientProfile): number {
  const bmr = calculateBMR(profile);
  const multiplier = ACTIVITY_MULTIPLIERS[profile.activity_level];
  return Math.round(bmr * multiplier);
}

/**
 * Calculate lean body mass (if body fat % is known)
 */
export function calculateLeanMass(weight_lbs: number, body_fat_percentage: number): number {
  return weight_lbs * (1 - body_fat_percentage / 100);
}

/**
 * Calculate macro targets from calories and preferences
 */
export function calculateMacros(
  target_calories: number,
  weight_lbs: number,
  settings: GoalSettings
): MacroTargets {
  // Protein: grams per lb of body weight
  const protein_g = Math.round(weight_lbs * settings.protein_per_lb);
  const protein_calories = protein_g * 4;

  // Fat: grams per lb of body weight (minimum for health)
  const fat_per_lb = Math.max(settings.fat_per_lb, MIN_FAT_PER_LB);
  const fat_g = Math.round(weight_lbs * fat_per_lb);
  const fat_calories = fat_g * 9;

  // Carbs: remaining calories
  const remaining_calories = target_calories - protein_calories - fat_calories;
  const carbs_g = Math.max(0, Math.round(remaining_calories / 4));

  // Fiber: 14g per 1000 calories
  const fiber_g = Math.round((target_calories / 1000) * FIBER_PER_1000_CAL);

  // Water: 0.5 oz per lb body weight
  const water_oz = Math.round(weight_lbs * WATER_OZ_PER_LB);

  return {
    calories: target_calories,
    protein_g,
    carbs_g,
    fat_g,
    fiber_g,
    water_oz,
  };
}

/**
 * Calculate expected weekly weight change based on calorie adjustment
 * 3500 calories ≈ 1 lb of body weight
 */
export function calculateWeeklyChange(calorie_adjustment: number): number {
  return (calorie_adjustment * 7) / 3500;
}

/**
 * Full macro calculation from profile and settings
 */
export function calculateFullPlan(
  profile: ClientProfile,
  settings: GoalSettings,
  baseline_override?: number
): CalculationResult {
  const bmr = calculateBMR(profile);
  const tdee = baseline_override || calculateTDEE(profile);
  const target_calories = tdee + settings.calorie_adjustment;
  const targets = calculateMacros(target_calories, profile.weight_lbs, settings);
  const weekly_change_lbs = calculateWeeklyChange(settings.calorie_adjustment);

  // Calculate macro percentages
  const total_calories =
    targets.protein_g * 4 +
    targets.carbs_g * 4 +
    targets.fat_g * 9;

  const macros_percentage = {
    protein: Math.round((targets.protein_g * 4 / total_calories) * 100),
    carbs: Math.round((targets.carbs_g * 4 / total_calories) * 100),
    fat: Math.round((targets.fat_g * 9 / total_calories) * 100),
  };

  return {
    bmr,
    tdee,
    baseline_source: baseline_override ? 'food_log' : 'formula',
    baseline_confidence: baseline_override ? 'high' : 'medium',
    target_calories,
    targets,
    weekly_change_lbs,
    macros_percentage,
  };
}

// =============================================================================
// Default Settings Generators
// =============================================================================

/**
 * Get recommended default settings based on goal
 */
export function getDefaultSettings(goal: Goal, weight_lbs: number): GoalSettings {
  const proteinRec = PROTEIN_RECOMMENDATIONS[goal];

  let calorie_adjustment: number;
  switch (goal) {
    case 'fat_loss':
      calorie_adjustment = CALORIE_PRESETS.fat_loss.conservative.adjustment;
      break;
    case 'muscle_gain':
      calorie_adjustment = CALORIE_PRESETS.muscle_gain.lean.adjustment;
      break;
    default:
      calorie_adjustment = 0;
  }

  return {
    goal,
    calorie_adjustment,
    protein_per_lb: proteinRec.default,
    fat_per_lb: MIN_FAT_PER_LB,
  };
}

/**
 * Get smart protein recommendation based on multiple factors
 */
export function getSmartProteinRecommendation(
  goal: Goal,
  body_fat_percentage?: number
): { value: number; reason: string } {
  const base = PROTEIN_RECOMMENDATIONS[goal];

  // If high body fat, can use lower end since fat mass doesn't need protein
  if (body_fat_percentage && body_fat_percentage > 25) {
    return {
      value: base.min,
      reason: 'Lower range appropriate for higher body fat percentage',
    };
  }

  // If lean and losing fat, higher protein to preserve muscle
  if (goal === 'fat_loss' && body_fat_percentage && body_fat_percentage < 15) {
    return {
      value: base.max,
      reason: 'Higher protein to preserve muscle mass during aggressive cut',
    };
  }

  return {
    value: base.default,
    reason: base.note,
  };
}

// =============================================================================
// Validation & Guardrails
// =============================================================================

/**
 * Validate that macro plan is healthy and sustainable
 */
export function validatePlan(result: CalculationResult): {
  valid: boolean;
  warnings: string[];
} {
  const warnings: string[] = [];

  // Minimum calorie thresholds
  if (result.target_calories < 1200) {
    warnings.push('Target calories below 1200 - may be too aggressive for sustainability');
  }

  // Maximum deficit
  if (result.weekly_change_lbs < -2) {
    warnings.push('Projected loss exceeds 2 lbs/week - risk of muscle loss');
  }

  // Minimum protein
  if (result.targets.protein_g < 50) {
    warnings.push('Protein below minimum recommended (50g)');
  }

  // Minimum fat for hormonal health
  if (result.targets.fat_g < 40) {
    warnings.push('Fat intake may be too low for hormonal health');
  }

  // Negative carbs (shouldn't happen with our math, but safety check)
  if (result.targets.carbs_g < 0) {
    warnings.push('Invalid macro distribution - carbs cannot be negative');
  }

  return {
    valid: warnings.length === 0,
    warnings,
  };
}

// =============================================================================
// Formatting Helpers
// =============================================================================

/**
 * Format weight change for display
 */
export function formatWeeklyChange(lbs: number): string {
  const absLbs = Math.abs(lbs);
  const direction = lbs < 0 ? 'lose' : lbs > 0 ? 'gain' : 'maintain';

  if (direction === 'maintain') {
    return 'Maintain weight';
  }

  return `${direction === 'lose' ? '-' : '+'}${absLbs.toFixed(1)} lb/week`;
}

/**
 * Format calorie adjustment for display
 */
export function formatCalorieAdjustment(adjustment: number): string {
  if (adjustment === 0) return 'Maintenance';
  const prefix = adjustment > 0 ? '+' : '';
  return `${prefix}${adjustment} cal/day`;
}
